import {AbstractInteractionMode, DrawModeOption, DrawStyleUtil} from "./abstract-interaction-mode";
import {Feature, Geometry} from "geojson";
import {v4 as uuid} from 'uuid';
import {EditorDeleteSelect} from "../interaction/editor/editor-delete-select";
import {EditorSimpleSelect} from "../interaction/editor/editor-simple-select";
import {EditorDrawParallelogram} from "../interaction/editor/editor-draw-parallelogram";
import {EditorPointTransformTranslate} from "../interaction/editor/editor-point-transform-translate";
import {EditorFeatureTransformTranslate} from "../interaction/editor/editor-feature-transform-translate";
import {EditorFeatureTransformScale} from "../interaction/editor/editor-feature-transform-scale";
import {EditorFeatureTransformRotate} from "../interaction/editor/editor-feature-transform-rotate";
import {EditorVertexTransformTranslate} from "../interaction/editor/editor-vertex-transform-translate";
import {EditorVertexDraw} from "../interaction/editor/editor-vertex-draw";
import {EditorVertexDelete} from "../interaction/editor/editor-vertex-delete";
import {EditorClone} from "../interaction/editor/editor-clone";
import {EditorSplitPolygon} from "../interaction/editor/editor-split-polygon";
import {EditorSplitLinestring} from "../interaction/editor/editor-split-linestring";
import {EditorBuffer} from "../interaction/editor/editor-buffer";
import {EditorOffsetLine} from "../interaction/editor/editor-offset-line";
import {LayerGeometry} from "../../document/model/layer";
import {EditorDrawPoint} from "../interaction/editor/editor-draw-point";
import {EditorDrawLinestring} from "../interaction/editor/editor-draw-linestring";
import {EditorDrawPolygon} from "../interaction/editor/editor-draw-polygon";
import {InteractionState} from "../interaction/abstract-interaction";
import store from "../../store";
import {VueComponentControl} from "../control/VueComponentControl";
import MapButton from "../../../components/map/MapButton.vue";
import {MapEvent} from "../../document/model/map";
import {MenuItem} from "primevue/menuitem";
import MapboxDraw from "@mapbox/mapbox-gl-draw";

export class DrawerMode extends AbstractInteractionMode {
  static identifier: string = "drawer";
  static title: string = "Desene temporare";
  static icon: string = "/icons/toolbox/drawer.svg";
  static allowChanges: boolean = true;

  static drawStyles = DrawStyleUtil.drawStyle({
    "line-color": '#23B402',
    "border-color": '#23B402',
    "line-width": 10
  });

  static defaultInteraction: string = 'draw_polygon';

  static interactions = {
    'draw_polygon': EditorDrawPolygon.definition({selectionAllowed: false, changeset: 'draw_polygon'}),
    'draw_linestring': EditorDrawLinestring.definition({changeset: 'draw_linestring'}),
    'draw_point': EditorDrawPoint.definition({changeset: 'draw_point'}),
    'select': EditorSimpleSelect.definition({selectionAllowed: true, multiSelection: true}),
    'editor_select': EditorSimpleSelect.definition({selectionAllowed: true, multiSelection: true}),
    'delete_select': EditorDeleteSelect.definition({selectionAllowed: true, multiSelection: true}),
    'draw_parallelogram': EditorDrawParallelogram.definition({
      selectionAllowed: false,
      changeset: 'draw_paralellogram'
    }),
    'point_transform_translate': EditorPointTransformTranslate.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'feature_transform_translate': EditorFeatureTransformTranslate.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'feature_transform_scale': EditorFeatureTransformScale.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'feature_transform_rotate': EditorFeatureTransformRotate.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'vertex_transform_translate': EditorVertexTransformTranslate.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'vertex_draw': EditorVertexDraw.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'vertex_delete': EditorVertexDelete.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'clone': EditorClone.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform'
    }),
    'split_polygon': EditorSplitPolygon.definition({
      selectionAllowed: false,
      selectionRequired: true,
      changeset: 'split_polygon'
    }),
    'split_linestring': EditorSplitLinestring.definition({
      selectionAllowed: false,
      selectionRequired: true,
      changeset: 'split_linestring',
    }),
    'buffer': EditorBuffer.definition({
      selectionAllowed: false,
      selectionRequired: true,
      changeset: 'buffer',
    }),
    'offset_line': EditorOffsetLine.definition({
      selectionAllowed: false,
      selectionRequired: true,
      changeset: 'offset_line'
    }),
  }

  onInit(): void {
    this.setupLayerGeometryRemoveHandler();
  }

  changeMapInteraction(interactionMode) {
    this.defaultInteraction = interactionMode.interactionMode
    this.controller.configuration.tmpInteractionLayerId = interactionMode.layerId;
    this.controller.configuration.drawStyle = interactionMode.drawStyle;
    this.drawStyles = DrawStyleUtil.drawStyle(interactionMode.drawStyle);
    this.controller.interaction.mode.changeInteraction(!this.controller?.configuration?.readOnly? this.defaultInteraction:'select');
    this._mapInstance.fire(MapEvent.Tick);
  }

  async onLayerGeometriesCreate(features: Feature[]) {
    this.controller.interaction.addTmpGeometries(
      features.map(feature => {
        return {
          id: uuid(),
          ...feature,
          documentId: this.controller?.interaction?.configuration?.tmpInteractionLayerId | undefined,
          properties: this.controller.interaction.getConfigurationDrawStyle()
        }
      })
    );
    this.controller.interaction.mode.resetInteraction();
  }

  async onLayerGeometriesUpdate(layerGeometry: LayerGeometry, feature: Feature) {
    feature.id = feature.id || layerGeometry.geometryId || layerGeometry.id;
    let featureState = this.controller.interaction.mode.currentInteractionState.getFeature(feature.id)
    featureState.geometry = feature.geometry
    feature.documentId = this.controller?.interaction?.configuration?.tmpInteractionLayerId | undefined
    this.controller.interaction.updateTmpGeometry(feature);

  }

  changeInteraction(interactionIdentifier: string, state?: InteractionState | undefined): void {
    super.changeInteraction(interactionIdentifier, state);
  }

  async saveLayerGeometry(layerGeometry: LayerGeometry, feature: Feature) {
    layerGeometry.geometry.geometry = JSON.parse(JSON.stringify(feature.geometry));
    layerGeometry.attributes = JSON.parse(JSON.stringify(feature.properties));
    let result: LayerGeometry = await store.dispatch("updateGeometry", {
      query: {
        asFeature: true,
      },
      data: {
        geometryId: layerGeometry.geometryId,
        geometry: feature,
        attributes: layerGeometry.attributes ?? {},
      },
      headers: {
        'x-access-token': this.controller.configuration.accessToken
      }
    }).catch((error) => {
      this.controller.configuration.toastProvider({
        severity: "error",
        summary: "Error",
        detail: this.controller.configuration.translatorProvider(error),
        life: 5000,
      });
    });
    return result;
  }

  public setupLayerGeometryRemoveHandler(): void {
    this._mapInstance.on(
      MapEvent.LayerGeometryRemove,
      (event: {features: LayerGeometry[]}) => {
        this.controller.configuration.confirmProvider().require({
          header: this.controller.configuration.translatorProvider("Delete?"),
          message: this.controller.configuration.translatorProvider("Are you sure want to delete the selected features?"),
          icon: "pi pi-exclamation-triangle",
          accept: () => {
            this.controller.interaction.deleteTmpGeometries(event.features.map(layerGeometry => layerGeometry.geometryId))
          },
          reject: () => {

          },
        });
      }
    );
  }

  getInteractionModeActions(interactionIdentifier: string, point?: mapboxgl.Point | undefined): MenuItem[] {
    let options: any = [];
    if (this.controller?.configuration?.mapInteractions?.length > 1) {
      this.controller?.configuration?.mapInteractions.forEach((intMode, index) => {

        options.push({
          label: intMode.title,
          icon: intMode.icon,
          active: this.controller?.configuration?.tmpInteractionLayerId === intMode.layerId,
          command: () => this.changeMapInteraction(intMode)
        });
      });
    }

    return options;
  }

  getGlobalActions(drawMode) {
    let options: any = [];

    options.push({
      label: "Select",
      icon: "/icons/toolbox/editor-select.svg",
      active: drawMode === "select",
      command: () => this.changeInteraction("select"),
      pos: { position: 'bottom' }
    });

    if (!this.controller?.configuration?.readOnly) {
      options.push({
        label: "Edit attributes",
        icon: "/icons/toolbox/table-edit-icon.svg",
        active: drawMode === "editor_select",
        command: () => this.changeInteraction("editor_select"),
        pos: { position: 'bottom' }
      });

      switch (this.defaultInteraction || 'draw_polygon') {
        case "draw_polygon":
          options.push(
            {
              label: "Add new polygon",
              icon: "/icons/toolbox/polygon-draw.svg",
              command: () => this.changeInteraction("draw_polygon"),
              active: drawMode === "draw_polygon",
              pos: { position: 'bottom' }
            }
          );
          options.push(
            {
              label: "Add parallelogram grid",
              icon: "/icons/toolbox/grid.svg",
              command: () => this.changeInteraction("draw_parallelogram"),
              active: drawMode === "draw_parallelogram",
              pos: { position: 'bottom' }
            }
          );
          break;
        case "draw_linestring":
          options.push(
            {
              label: "Add new line",
              icon: "/icons/toolbox/polyline-pt.svg",
              command: () => this.changeInteraction("draw_linestring"),
              active: drawMode === "draw_linestring",
              pos: { position: 'bottom' }
            }
          );
          break;
        case "draw_point":
          options.push(
            {
              label: "Add new point",
              icon: "/icons/toolbox/point.svg",
              command: () => this.changeInteraction("draw_point"),
              active: drawMode === "draw_point",
              pos: { position: 'bottom' }
            }
          );
          break;
        default:
          this.controller?.configuration?.toastProvider({
            severity: "error",
            summary: "Error",
            detail: this.controller?.configuration?.translatorProvider(`Unknown shapeType!`),
            life: 5000,
          });
        //throw new Error(`Unknown shapeType ${this.layer.properties?.shapeType?.title}`);
      }

      options.push({
        label: "Delete",
        icon: "/icons/toolbox/delete.svg",
        command: () => this.changeInteraction("delete_select"),
        active: drawMode === "delete_select",
        pos: { position: 'bottom' }
      })
    }
    /*this.removeControls()
    if (this.controller?.configuration?.mapInteractions?.length>1) {
      this.controller?.configuration?.mapInteractions.forEach((intMode,index) => {
        let control = new VueComponentControl(MapButton, {label: intMode.title, active: this.currentInteractionIdentifier === intMode.interactionMode, compatibilityFn: (event,data) => {this.changeMapInteraction(event,intMode)}})
        this.addControl(control, 'top-left');
      });
    }*/

    return options;
  }

  getSingleActions(layerGeometry, interactionIdentifier, point: mapboxgl.Point | undefined = undefined) {
    let options: any = [];
    if (!this.controller?.configuration?.readOnly)
      if (layerGeometry.geometry.geometry.type !== "Point") {

        options.push(
          {
            label: "Translate transform",
            icon: "/icons/toolbox/move.svg",
            command: () => this.changeInteraction("feature_transform_translate"),
            active: interactionIdentifier === "feature_transform_translate",
            pos: { position: 'bottom' }
          },
        );
        options.push(
          {
            label: "Scale transform",
            icon: "/icons/toolbox/vector-scale.svg",
            command: () => this.changeInteraction("feature_transform_scale"),
            active: interactionIdentifier === "feature_transform_scale",
            pos: { position: 'bottom' }
          },
        );

        options.push(
          {
            label: "Rotate transform",
            icon: "/icons/toolbox/vector-rotate.svg",
            command: () => this.changeInteraction("feature_transform_rotate"),
            active: interactionIdentifier === "feature_transform_rotate",
            pos: { position: 'bottom' }
          },
        );
        options.push(
          {
            label: "Move vertex",
            icon: "/icons/toolbox/vertex-move.svg",
            command: () => this.changeInteraction("vertex_transform_translate"),
            active: interactionIdentifier === "vertex_transform_translate",
            pos: { position: 'bottom' }
          }
        );
        options.push({
          label: "Add vertex",
          icon: "/icons/toolbox/vertex-add.svg",
          command: () => this.changeInteraction("vertex_draw"),
          active: interactionIdentifier === "vertex_draw",
          pos: { position: 'bottom' }
        });
        options.push({
          label: "Delete vertex",
          icon: "/icons/toolbox/vertex-delete.svg",
          command: () => this.changeInteraction("vertex_delete"),
          active: interactionIdentifier === "vertex_delete",
          pos: { position: 'bottom' }
        });
        options.push({
          label: "Clone",
          icon: "/icons/toolbox/clone-geometry.svg",
          command: () => this.changeInteraction("clone"),
          active: interactionIdentifier === "clone",
            pos: { position: 'bottom' }
        }
        );
        options.push({
          label: 'Buffer',
          icon: "/icons/toolbox/line-buffer.svg",
          command: () => this.changeInteraction("buffer"),
          active: interactionIdentifier === "buffer",
          pos: { position: 'bottom' }
        })
        switch (layerGeometry.geometry.geometry.type) {
          case 'Polygon':
          case 'MultiPolygon':
            options.push({
              label: "Split",
              icon: "/icons/toolbox/polygon-split.svg",
              command: () => this.changeInteraction("split_polygon"),
              active: interactionIdentifier === "split_polygon",
              pos: { position: 'bottom' }
            });
            break;
          case 'MultiLineString':
          case 'LineString':
            options.push({
              label: "Split",
              icon: "/icons/toolbox/line-split.svg",
              command: () => this.changeInteraction("split_linestring"),
              active: interactionIdentifier === "split_linestring",
              pos: { position: 'bottom' }
            });
            options.push({
              label: 'Offset',
              icon: "/icons/toolbox/line-offset.svg",
              command: () => this.changeInteraction("offset_line"),
              active: interactionIdentifier === "offset_line",
              pos: { position: 'bottom' }
            })
            break;
        }


      } else {
        options.push(
          {
            label: "Move point",
            icon: "/icons/toolbox/moveVertex.png",
            command: () => this.changeInteraction("point_transform_translate"),
            active: interactionIdentifier === "point_transform_translate",
            pos: { position: 'bottom' }
          }
        );
        options.push({
          label: "Clone",
          icon: "/icons/toolbox/clone-geometry.svg",
          command: () => this.changeDrawMode("clone", {featureId: layerGeometry.geometryId}),
          active: interactionIdentifier === "clone",
            pos: { position: 'bottom' }
        }
        );
      }

    return options;
  }

  getMultiActions(layerGeometries, drawMode, point: mapboxgl.Point | undefined = undefined) {
    let actions: any[] = [];
    if (!this.controller?.configuration?.readOnly)
      if (layerGeometries.length >= 2) {
        actions.unshift({
          label: "Merge",
          icon: "/icons/toolbox/merge.svg",
          command: () => this._mapInstance.fire("layer-geometry-merge", {features: layerGeometries}),
          pos: { position: 'bottom' }
        });
      }

    return actions;
  }

  setDrawStyle(drawStyle) {
    this.drawStyles = DrawStyleUtil.drawStyle(drawStyle);
  }

  onDestroy() {
    super.onDestroy();
    this.removeControls()
  }

}
